import { createContext, useContext, useEffect, useState } from "react";
import { supabase, describeError } from "@/lib/supabase";
import { authRedirectUrl } from "@/lib/site";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = loading, false = signed out, object = signed in
  const [user, setUser] = useState(null);
  const [info, setInfo] = useState("");

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      const u = data?.session?.user;
      setUser(u ? toUser(u) : false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      const u = session?.user;
      setUser(u ? toUser(u) : false);
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  const login = async (email, password) => {
    setInfo("");
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw new Error(describeError(error));
    return toUser(data.user);
  };

  const register = async (email, password, name) => {
    setInfo("");
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { name, full_name: name, display_name: name },
        emailRedirectTo: authRedirectUrl("/auth"),
      },
    });
    if (error) throw new Error(describeError(error));
    if (!data.session) {
      // Email confirmation required by the project — surface a friendly message
      setInfo("Check your email to confirm your account, then sign in.");
      return null;
    }
    return toUser(data.user);
  };

  const signInWithGoogle = async () => {
    setInfo("");
    // Fetch the OAuth /authorize URL WITHOUT letting supabase-js navigate the
    // browser automatically — otherwise a server-side provider misconfig
    // returns a 400 JSON body that replaces our SPA and the catch below
    // never fires.
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: authRedirectUrl("/dashboard"),
        skipBrowserRedirect: true,
      },
    });
    if (error) throw new Error(describeError(error));
    const url = data?.url;
    if (!url) {
      throw new Error("Google Sign-In isn't available right now.");
    }
    // Preflight the /authorize URL so we can catch server-side provider
    // misconfig (e.g. missing OAuth secret) before the browser navigates.
    try {
      const resp = await fetch(url, {
        method: "GET",
        redirect: "manual",
        credentials: "omit",
        mode: "cors",
      });
      // If the response has a body (i.e. Supabase returned an error page
      // instead of a redirect), parse it and surface the message.
      if (resp.status >= 400 && resp.type !== "opaqueredirect") {
        let msg = "Google Sign-In failed.";
        try {
          const body = await resp.json();
          msg = body?.msg || body?.error_description || body?.error || msg;
        } catch {
          /* non-JSON body — keep generic message */
        }
        throw new Error(msg);
      }
    } catch (e) {
      // Network / CORS errors during preflight → treat as unknown failure,
      // let the caller decide what to show.
      if (e instanceof Error && e.message && e.message !== "Failed to fetch") {
        throw e;
      }
      // If preflight itself failed for network reasons, fall through to
      // navigation (the browser will try the real redirect anyway).
    }
    window.location.assign(url);
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(false);
  };

  const sendPasswordReset = async (email) => {
    setInfo("");
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: authRedirectUrl("/reset-password"),
    });
    if (error) throw new Error(describeError(error));
  };

  return (
    <AuthContext.Provider value={{ user, login, register, signInWithGoogle, logout, sendPasswordReset, info, setInfo }}>
      {children}
    </AuthContext.Provider>
  );
}

function toUser(u) {
  if (!u) return false;
  const name =
    u.user_metadata?.name ||
    u.user_metadata?.full_name ||
    u.user_metadata?.display_name ||
    u.email?.split("@")[0] ||
    "Caregiver";
  return {
    id: u.id,
    email: u.email,
    name,
    role: u.user_metadata?.role || "user",
  };
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export { describeError as formatApiErrorDetail };
